import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { createAuthenticatedClient } from '@/lib/graphql';
import { useAuth } from '@/lib/auth/AuthContext';

// Types
export interface SourceSettings {
  id: string;
  configId: string;
  bingResultCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateSourceSettingsInput {
  bingResultCount: number;
}

// Queries
const GET_SOURCE_SETTINGS = gql`
  query GetSourceSettings {
    sourceSettings {
      id
      configId
      bingResultCount
      createdAt
      updatedAt
    }
  }
`;

// Mutations
const UPDATE_SOURCE_SETTINGS = gql`
  mutation UpdateSourceSettings($input: UpdateSourceSettingsInput!) {
    updateSourceSettings(input: $input) {
      sourceSettings {
        id
        configId
        bingResultCount
        createdAt
        updatedAt
      }
    }
  }
`;

// Hooks
export function useSourceSettings() {
  const { accessToken } = useAuth();

  return useQuery({
    queryKey: ['sourceSettings'],
    queryFn: async () => {
      if (!accessToken) throw new Error('Not authenticated');
      const client = createAuthenticatedClient(accessToken);
      const data = await client.request<{ sourceSettings: SourceSettings }>(
        GET_SOURCE_SETTINGS
      );
      return data.sourceSettings;
    },
    enabled: !!accessToken,
  });
}

export function useUpdateSourceSettings() {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateSourceSettingsInput) => {
      if (!accessToken) throw new Error('Not authenticated');
      const client = createAuthenticatedClient(accessToken);
      const data = await client.request<{
        updateSourceSettings: {
          sourceSettings: SourceSettings;
        };
      }>(UPDATE_SOURCE_SETTINGS, { input });
      return data.updateSourceSettings.sourceSettings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sourceSettings'] });
    },
  });
}
